import { useEffect, useState } from 'react';
import { trpc } from '@web/lib/trpc';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@web/components/ui/dialog';
import { Button } from '@web/components/ui/button';
import { Input } from '@web/components/ui/input';
import { Label } from '@web/components/ui/label';
import { Spinner } from '@web/components/ui/spinner';
import { toast } from 'sonner';
import { Checkbox } from '@web/components/ui/checkbox';

type UserDetailsDialogProps = {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function UserDetailsDialog({
  userId,
  open,
  onOpenChange,
}: UserDetailsDialogProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const utils = trpc.useUtils();

  // Fetch user data
  const { data: userData, isLoading: isLoadingUser } =
    trpc.user.getUser.useQuery({ id: userId }, { enabled: !!userId && open });

  // Fetch user roles
  const { data: userRoles, isLoading: isLoadingRoles } =
    trpc.user.getUserRoles.useQuery({ userId }, { enabled: !!userId && open });

  // Fetch all available roles
  const { data: allRoles, isLoading: isLoadingAllRoles } =
    trpc.user.listRoles.useQuery();

  // Update user mutation
  const updateUserMutation = trpc.user.updateUser.useMutation({
    onSuccess: () => {
      // Invalidate queries to refresh data
      void utils.user.getUser.invalidate({ id: userId });
      void utils.user.getUserRoles.invalidate({ userId });
      void utils.user.listUsers.invalidate();

      toast.success('User has been updated successfully.');

      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update user. Please try again.');
    },
  });

  // Initialize form with user data when it's loaded
  useEffect(() => {
    if (userData) {
      setFirstName(userData.firstName);
      setLastName(userData.lastName ?? '');
      setEmail(userData.email ?? '');
    }
  }, [userData]);

  // Initialize selected roles when they're loaded
  useEffect(() => {
    if (userRoles) {
      setSelectedRoleIds(userRoles.map((role) => role.roleId));
    }
  }, [userRoles]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    updateUserMutation.mutate({
      userId,
      userData: {
        firstName,
        lastName,
        email,
        roleIds: selectedRoleIds,
      },
    });
  };

  // Handle role selection toggle
  const handleRoleToggle = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId],
    );
  };

  // Loading state
  const isLoading = isLoadingUser || isLoadingRoles || isLoadingAllRoles;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="block max-h-[90vh] w-[90vw] max-w-none overflow-y-auto rounded-lg border-gray-200 p-6 shadow-lg md:w-[80vw] md:p-0 lg:w-[60vw] lg:p-0">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-left">Edit User</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                    }}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                    }}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Roles</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {allRoles?.map((role) => (
                    <div
                      key={role.roleId}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`role-${role.roleId}`}
                        checked={selectedRoleIds.includes(role.roleId)}
                        onCheckedChange={() => {
                          handleRoleToggle(role.roleId);
                        }}
                      />
                      <Label
                        htmlFor={`role-${role.roleId}`}
                        className="text-sm font-normal"
                      >
                        {role.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateUserMutation.isPending}>
                {updateUserMutation.isPending ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
